import {
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  Church as ChurchIcon,
  LoaderCircle,
  Search,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { SearchHighlight } from '@/components/ui/search-highlight';
import { LoadingState } from '@/components/ui/loading';
import { useGiving } from './giving';
import { useChurchCopy } from './church-copy';
import {
  churchPaths,
  normalizeChurchSearch,
  useChurchHierarchy,
  type Church,
  type Conference,
  type ChurchHierarchy,
} from './church-data';
import { AsyncButton } from './ui';

function ChurchChoices({
  data,
  onSelect,
  saving,
  disabled,
}: {
  data: ChurchHierarchy;
  onSelect: (id: string) => void;
  saving: string | null | undefined;
  disabled: boolean;
}): JSX.Element {
  const giving = useGiving();
  const copy = useChurchCopy();
  const paths = churchPaths(data);
  const selected = paths.find(({ church }) => church.id === giving.churchId);
  const [conferenceId, setConferenceId] = useState<string | null>(selected?.conference.id ?? null);
  const [search, setSearch] = useState('');
  const back = useRef<HTMLButtonElement>(null);
  const conferenceButtons = useRef(new Map<string, HTMLButtonElement>());
  const previousConference = useRef(conferenceId);
  const list = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const query = normalizeChurchSearch(search);
  const conference = data.unions
    .flatMap((union) => union.conferences)
    .find(({ id }) => id === conferenceId);
  const matches = query
    ? paths
        .filter(
          ({ church, conference: parent }) =>
            normalizeChurchSearch(church.name).includes(query) ||
            normalizeChurchSearch(parent.name).includes(query),
        )
        .sort(
          (a, b) =>
            Number(b.conference.id === conferenceId) - Number(a.conference.id === conferenceId),
        )
    : [];
  const searchGroups = new Map<string, { conference: Conference; churches: Church[] }>();
  for (const item of matches.slice(0, 100)) {
    const group = searchGroups.get(item.conference.id) ?? {
      conference: item.conference,
      churches: [],
    };
    group.churches.push(item.church);
    searchGroups.set(item.conference.id, group);
  }
  useEffect(() => {
    const previous = previousConference.current;
    if (previous === conferenceId) return;
    previousConference.current = conferenceId;
    const target = conferenceId ? back.current : conferenceButtons.current.get(previous ?? '');
    const scroller = list.current?.closest<HTMLElement>('.ui-dialog__body');
    if (scroller) scroller.scrollTop = 0;
    target?.focus({ preventScroll: conferenceId !== null });
  }, [conferenceId]);

  const churchRow = (church: Church): JSX.Element => (
    <Button
      variant="bare"
      className="church-choice"
      key={church.id}
      aria-pressed={church.id === giving.churchId}
      aria-label={church.name}
      disabled={disabled || saving !== undefined}
      onClick={() => onSelect(church.id)}
    >
      <span className="church-choice__icon">
        <ChurchIcon size={18} aria-hidden="true" />
      </span>
      <span className="church-choice__text">
        <strong>
          <SearchHighlight text={church.name} search={query} />
        </strong>
      </span>
      {saving === church.id ? (
        <LoaderCircle className="ui-spinner" size={18} aria-hidden="true" />
      ) : church.id === giving.churchId ? (
        <Check size={18} aria-hidden="true" />
      ) : (
        <ChevronRight size={16} aria-hidden="true" />
      )}
    </Button>
  );
  return (
    <div className="church-choices ui-stack">
      <p className="sr-only" id={hintId}>
        {copy.searchHint}
      </p>
      <div className="church-choices__toolbar ui-stack">
        {conference && (
          <div className="church-browse-heading">
            <Button
              ref={back}
              variant="ghost"
              size="icon"
              aria-label={copy.backConferences}
              disabled={disabled || saving !== undefined}
              onClick={() => {
                setConferenceId(null);
                setSearch('');
              }}
            >
              <ArrowLeft size={19} aria-hidden="true" />
            </Button>
            <h3 aria-label={conference.name}>
              <SearchHighlight text={conference.name} search={query} />
            </h3>
          </div>
        )}
        <Field>
          <span className="sr-only">{copy.search}</span>
          <div className="church-choices__search">
            <Search size={19} aria-hidden="true" />
            <Input
              type="search"
              placeholder={copy.search}
              aria-describedby={hintId}
              value={search}
              disabled={disabled || saving !== undefined}
              autoComplete="off"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </Field>
      </div>
      <div ref={list} className="church-choices__results" aria-busy={saving !== undefined}>
        {query ? (
          <>
            {[...searchGroups.values()].map((group) => (
              <section className="church-choice-group" key={group.conference.id}>
                {group.conference.id !== conferenceId && (
                  <h3 className="church-choice-group__label" aria-label={group.conference.name}>
                    <SearchHighlight text={group.conference.name} search={query} />
                  </h3>
                )}
                {group.churches.map(churchRow)}
              </section>
            ))}
            {!matches.length && <p role="status">{copy.noResults}</p>}
            {matches.length > 100 && <p className="church-choices__hint">{copy.narrowSearch}</p>}
          </>
        ) : conference ? (
          <>
            {conference.churches.map(churchRow)}
            {!conference.churches.length && <p role="status">{copy.noChurches}</p>}
          </>
        ) : (
          <>
            {data.unions
              .filter((union) => union.conferences.length > 0)
              .map((union) => (
                <section className="church-choice-group" key={union.id}>
                  <h3 className="church-choice-group__label">{union.name}</h3>
                  {union.conferences.map((item) => (
                    <Button
                      variant="bare"
                      className="church-choice church-choice--conference"
                      key={item.id}
                      ref={(node) => {
                        if (node) conferenceButtons.current.set(item.id, node);
                        else conferenceButtons.current.delete(item.id);
                      }}
                      disabled={disabled || saving !== undefined}
                      onClick={() => {
                        setConferenceId(item.id);
                      }}
                    >
                      <span className="church-choice__icon">
                        <Building2 size={18} aria-hidden="true" />
                      </span>
                      <span className="church-choice__text">
                        <strong>{item.name}</strong>
                      </span>
                      <ChevronRight size={16} aria-hidden="true" />
                    </Button>
                  ))}
                </section>
              ))}
            {!data.unions.some((union) => union.conferences.length > 0) && (
              <p role="status">{copy.noConferences}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ChurchPicker({ disabled = false }: { disabled?: boolean }): JSX.Element {
  const giving = useGiving();
  const copy = useChurchCopy();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState<string | null>();
  const [failed, setFailed] = useState(false);
  const command = useRef(false);
  const hierarchy = useChurchHierarchy(open || !!giving.churchId);
  const selected = churchPaths(hierarchy.data).find(({ church }) => church.id === giving.churchId);
  const selectedName =
    selected?.church.name ?? (hierarchy.isPending ? copy.loading : copy.myChurch);
  const save = (id: string | null): void => {
    if (disabled || command.current) return;
    command.current = true;
    setSaving(id);
    setFailed(false);
    void giving
      .selectChurch(id)
      .then(() => setOpen(false))
      .catch(() => setFailed(true))
      .finally(() => {
        command.current = false;
        setSaving(undefined);
      });
  };
  return (
    <div className="church-picker">
      <Button
        variant="bare"
        className={`church-picker__trigger${selected ? ' church-picker__trigger--selected' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled || saving !== undefined}
        onClick={() => {
          setFailed(false);
          setOpen(true);
        }}
      >
        {selected ? (
          <ChurchIcon size={23} aria-hidden="true" />
        ) : (
          <Search size={21} aria-hidden="true" />
        )}
        <span>
          <small>{copy.optionalChurch}</small>
          <strong>{giving.churchId ? selectedName : copy.noSelection}</strong>
        </span>
        <small>{giving.churchId ? copy.change : copy.chooseAction}</small>
        <ChevronRight size={19} aria-hidden="true" />
      </Button>
      {open && (
        <ResponsiveDialog
          title={copy.choose}
          closeLabel={copy.close}
          onClose={() => setOpen(false)}
          footer={
            <Button
              variant="secondary"
              fullWidth
              disabled={disabled || saving !== undefined}
              loading={saving === null}
              onClick={() => save(null)}
            >
              {copy.noSelection}
            </Button>
          }
        >
          {hierarchy.isPending && <LoadingState label={copy.loading} count={5} />}
          {hierarchy.isError && (
            <div className="ui-stack">
              <p role="alert">{copy.loadFailed}</p>
              <Button
                variant="secondary"
                disabled={disabled || saving !== undefined}
                onClick={() => {
                  void hierarchy.refetch();
                }}
              >
                {copy.retry}
              </Button>
            </div>
          )}
          {hierarchy.data && (
            <ChurchChoices
              data={hierarchy.data}
              saving={saving}
              disabled={disabled}
              onSelect={save}
            />
          )}
          {saving !== undefined && <p role="status">{copy.saving}</p>}
          {failed && (
            <p className="giving-error" role="alert">
              {copy.saveFailed}
            </p>
          )}
        </ResponsiveDialog>
      )}
    </div>
  );
}

export function SaveChurchButton({ id }: { id: string }): JSX.Element {
  const giving = useGiving();
  const copy = useChurchCopy();
  const selected = giving.churchId === id;
  return (
    <AsyncButton
      variant="secondary"
      startIcon={selected ? <Check size={16} aria-hidden="true" /> : undefined}
      action={() => giving.selectChurch(id)}
    >
      {selected ? copy.myChurch : copy.choose}
    </AsyncButton>
  );
}
